<template>
	<div v-if="summary" class="flex items-center gap-2">
		<div v-if="showSymbols && symbolPupils.length > 0" class="flex items-center shrink-0" data-test="symbols">
			<!-- Later symbols stack on top; the rightmost is fully opaque and the ones behind it fade.
			     Each sits on an opaque header-coloured squircle with the fade on the symbol inside,
			     so a faded symbol blends into the header instead of showing its neighbour -->

			<span
				v-for="(pupil, index) in symbolPupils"
				:key="pupil.id"
				class="block w-6 h-6 mask mask-squircle bg-base-100"
				:class="[index > 0 && selectedPupils.length > 4 ? '-ml-5' : index > 0 ? '-ml-3' : '']"
			>
				<PupilSymbol
					:pupil="pupil"
					data-test="symbol"
				/>
			</span>
		</div>
		<span>{{ summary }}</span>
	</div>
</template>

<script lang="ts">
import { PropType } from "vue";
import PupilSymbol from "@/components/ui/symbols/PupilSymbol.vue";
import { Pupil } from "@/models/Pupil.model";

const PUPILS_CUTOFF = 3;

/**
 * Describes a pupil selection against a population in a few words: "Everyone",
 * "Everyone, except Anna", "Only Anna and Bert", or a count. Optionally leads
 * with up to three fading pupil symbols. Pure presentation: no store access.
 */
export default {
	components: { PupilSymbol },

	props: {
		/** The selected pupils, or null when the selection is not known yet. */
		selectedPupils: {
			type: Array as PropType<Pupil[] | null>,
			default: null,
		},
		/** The pupils the selection is taken from; "Everyone" means all of these. */
		allPupils: {
			type: Array as PropType<Pupil[]>,
			required: true,
		},
		showSymbols: {
			type: Boolean,
			default: false,
		},
		cutoff: {
			type: Number,
			default: PUPILS_CUTOFF,
		},
	},

	computed: {
		selectedIds(): Set<string> {
			return new Set((this.selectedPupils ?? []).map((pupil: Pupil) => pupil.id));
		},

		selected(): Pupil[] {
			return this.allPupils.filter((pupil: Pupil) => this.selectedIds.has(pupil.id));
		},

		notSelected(): Pupil[] {
			return this.allPupils.filter((pupil: Pupil) => !this.selectedIds.has(pupil.id));
		},

		symbolPupils(): Pupil[] {
			return this.selected.slice(0, this.cutoff);
		},

		summary(): string {
			if (this.allPupils.length === 0 || this.selectedPupils === null) {
				return '';
			}

			if (this.selected.length === 0) {
				return this.$t('No pupils selected');
			}

			if (this.allPupils.length === 1) {
				return this.selected[0].firstName;
			}

			if (this.notSelected.length === 0) {
				return this.$t('Everyone');
			}

			if (this.notSelected.length <= this.cutoff) {
				return this.$t('Everyone, except {pupils}', { pupils: this.concatPupilNames(this.notSelected) });
			}

			if (this.selected.length <= this.cutoff) {
				return this.$t('Only {pupils}', { pupils: this.concatPupilNames(this.selected) });
			}

			return this.$t('{pupils} pupils', { pupils: this.selected.length });
		},
	},

	methods: {
		/** "Anna", "Anna and Bert", "Anna, Bert and Cleo". */
		concatPupilNames(pupils: Pupil[]): string {
			const names = pupils.map((pupil: Pupil) => pupil.firstName);
			if (names.length === 1) {
				return names[0];
			}
			const last = names.pop();
			return names.join(', ') + ' ' + this.$t('and') + ' ' + last;
		},
	},
}
</script>
